import { INGESTION_AUTHLESS_RESTRICTED_FIELD_TYPE_SET, type FieldType } from "../data.ingestion.const";
import type { IngestionPoint } from "../data.ingestion.service";
import { S25Icons } from "../../s25-icon/s25.icon.const";

export type IngestionPaletteGroupId = "1" | "2" | "3" | "4" | "6" | "7";

export const IngestionPaletteGroupIds = {
    BASIC_INPUT: "1",
    CHOICE: "2",
    RATING_AND_SCALE: "3",
    LAYOUT: "4",
    CONNECT25_REFERENCE: "6",
    LIVE25_FIELDS: "7",
} as const satisfies Record<string, IngestionPaletteGroupId>;

const BASIC_PALETTE_GROUP_IDS: readonly IngestionPaletteGroupId[] = [
    IngestionPaletteGroupIds.BASIC_INPUT,
    IngestionPaletteGroupIds.CHOICE,
    IngestionPaletteGroupIds.RATING_AND_SCALE,
    IngestionPaletteGroupIds.LAYOUT,
];

export type IngestionPaletteUseFilter = "all" | "basic" | "connect25" | "live25";

/** Min label length for optional `fullWidth` tiles (Event Categories is 16 and truncates at half width). */
export const PALETTE_FULL_WIDTH_MIN_LABEL_LENGTH = 16;

export const PALETTE_FULL_WIDTH_FIELD_TYPES: ReadonlySet<FieldType> = new Set([
    "ELECTION_POSITION",
    "ORG_TYPE",
    "ORG_RATING",
    "MULTISELECT_OCCURRENCES",
    "CATEGORIES",
    "CUSTOM_ATTRIBUTE",
    "REQUIREMENTS",
    "CONTACT_ROLE",
    "ORGANIZATION_MULTISELECT",
    "ORG_CATEGORIES",
    "ORG_CUSTOM_ATTRIBUTE",
    "ORG_CONTACT_ROLE",
    "LIVE_25_EVENT_FORM",
]);

export type IngestionPaletteTile = {
    fieldType: FieldType;
    label: string;
    icon: S25Icons.Name;
    externalLinkStyle?: boolean;
    fullWidth?: boolean;
};

export type IngestionPaletteGroup = {
    id: IngestionPaletteGroupId;
    name: string;
    defaultExpanded: boolean;
    tiles: IngestionPaletteTile[];
};

export const INGESTION_FIELD_PALETTE_GROUPS: readonly IngestionPaletteGroup[] = [
    {
        id: IngestionPaletteGroupIds.BASIC_INPUT,
        name: "Basic Input",
        defaultExpanded: true,
        tiles: [
            { fieldType: "TEXT", label: "User Input", icon: "paletteType" },
            { fieldType: "RICH_TEXT", label: "Rich Text", icon: "paletteAlignLeft" },
            { fieldType: "DURATION", label: "Duration", icon: "paletteClock" },
            { fieldType: "FILE", label: "File Upload", icon: "paletteUpload" },
            { fieldType: "IMAGE", label: "Image", icon: "paletteImage" },
        ],
    },
    {
        id: IngestionPaletteGroupIds.CHOICE,
        name: "Choice",
        defaultExpanded: true,
        tiles: [
            { fieldType: "SELECT", label: "Select", icon: "paletteChevronDown" },
            { fieldType: "MULTISELECT", label: "Multi-Select", icon: "paletteListChecks" },
            { fieldType: "CHECKBOX", label: "Checkbox", icon: "paletteSquareCheck" },
            { fieldType: "RADIOBUTTON", label: "Radio Button", icon: "paletteCircleDot" },
            { fieldType: "TOGGLE", label: "Toggle", icon: "paletteToggleRight" },
        ],
    },
    {
        id: IngestionPaletteGroupIds.RATING_AND_SCALE,
        name: "Rating and Scale",
        defaultExpanded: true,
        tiles: [
            { fieldType: "STARS_RATING", label: "Stars Rating", icon: "paletteStar" },
            { fieldType: "NUMERIC_SCALE", label: "Numeric Scale", icon: "paletteNumericScale" },
        ],
    },
    {
        id: IngestionPaletteGroupIds.LAYOUT,
        name: "Layout",
        defaultExpanded: true,
        tiles: [{ fieldType: "SECTION", label: "Section Header", icon: "paletteSectionHeader" }],
    },
    {
        id: IngestionPaletteGroupIds.CONNECT25_REFERENCE,
        name: "Connect25 Reference",
        defaultExpanded: false,
        tiles: [
            { fieldType: "FORM", label: "Form", icon: "paletteFileText" },
            { fieldType: "REGISTRANTS", label: "Registrants", icon: "paletteUsers" },
            {
                fieldType: "ELECTION_POSITION",
                label: "Election Position",
                icon: "paletteVote",
                fullWidth: true,
            },
        ],
    },
    {
        id: IngestionPaletteGroupIds.LIVE25_FIELDS,
        name: "25Live Fields",
        defaultExpanded: false,
        tiles: [
            { fieldType: "ORGANIZATION", label: "Organization", icon: "paletteOrganization" },
            { fieldType: "CONTACT", label: "Contact", icon: "paletteUserSearch" },
            { fieldType: "SECURITY_GROUP", label: "Security Group", icon: "paletteUsers" },
            { fieldType: "EVENT", label: "Event", icon: "paletteCalendarSearch" },
            { fieldType: "ROLE", label: "Org Role", icon: "paletteUserCog" },
            { fieldType: "ORG_TYPE", label: "Organization Type", icon: "paletteLayers", fullWidth: true },
            { fieldType: "ORG_RATING", label: "Organization Rating", icon: "paletteActivity", fullWidth: true },
            {
                fieldType: "MULTISELECT_OCCURRENCES",
                label: "Multiselect Occurrences",
                icon: "paletteCalendarCheck",
                fullWidth: true,
            },
            { fieldType: "EVENT_TYPE", label: "Event Type", icon: "paletteTag" },
            { fieldType: "OBJECT_OCCURRENCE", label: "Occurrences", icon: "paletteCalendarClock" },
            { fieldType: "CATEGORIES", label: "Event Categories", icon: "paletteTags", fullWidth: true },
            {
                fieldType: "CUSTOM_ATTRIBUTE",
                label: "Event Custom Attribute",
                icon: "paletteSlidersHorizontal",
                fullWidth: true,
            },
            { fieldType: "REQUIREMENTS", label: "Event Requirements", icon: "paletteClipboardList", fullWidth: true },
            { fieldType: "CONTACT_ROLE", label: "Event Contact Roles", icon: "paletteUserCheck", fullWidth: true },
            {
                fieldType: "ORGANIZATION_MULTISELECT",
                label: "Organization Multiselect",
                icon: "paletteOrganization",
                fullWidth: true,
            },
            {
                fieldType: "ORG_CATEGORIES",
                label: "Organization Categories",
                icon: "paletteFolderOpen",
                fullWidth: true,
            },
            {
                fieldType: "ORG_CUSTOM_ATTRIBUTE",
                label: "Organization Custom Attribute",
                icon: "paletteWrench",
                fullWidth: true,
            },
            {
                fieldType: "ORG_CONTACT_ROLE",
                label: "Organization Contact Roles",
                icon: "paletteUserCog",
                fullWidth: true,
            },
            {
                fieldType: "LIVE_25_EVENT_FORM",
                label: "25Live Event Form",
                icon: "paletteExternalLink",
                externalLinkStyle: true,
                fullWidth: true,
            },
        ],
    },
];

/** Sprite symbol ids referenced by palette tile icons (must exist in sprite.svg). */
export const INGESTION_PALETTE_SPRITE_SYMBOL_IDS: readonly string[] = [
    "palette--type",
    "palette--align-left",
    "palette--clock",
    "palette--upload",
    "palette--image",
    "chevron-down",
    "palette--list-checks",
    "palette--square-check",
    "palette--circle-dot",
    "palette--toggle-right",
    "palette--star",
    "palette--bar-chart-2",
    "palette--heading-1",
    "palette--building-2",
    "palette--user-search",
    "palette--calendar-search",
    "palette--user-cog",
    "palette--layers",
    "palette--activity",
    "palette--file-text",
    "palette--users",
    "palette--calendar-check",
    "palette--tag",
    "palette--calendar-clock",
    "palette--tags",
    "palette--sliders-horizontal",
    "palette--clipboard-list",
    "user-check",
    "palette--folder-open",
    "palette--wrench",
    "palette--vote",
    "palette--external-link",
];

const PALETTE_TILE_BY_FIELD_TYPE = new Map<FieldType, IngestionPaletteTile>(
    INGESTION_FIELD_PALETTE_GROUPS.flatMap((group) => group.tiles).map((tile) => [tile.fieldType, tile]),
);

export function getPaletteTileLabel(fieldType: FieldType): string | undefined {
    return PALETTE_TILE_BY_FIELD_TYPE.get(fieldType)?.label;
}

export function isPaletteTileVisibleForIngestionPoint(
    tile: IngestionPaletteTile,
    ingestionPoint: IngestionPoint | undefined,
): boolean {
    if (!ingestionPoint) {
        return true;
    }

    if (tile.fieldType === "ELECTION_POSITION" && !ingestionPoint.use?.electionCandidate) {
        return false;
    }

    if (tile.fieldType === "MULTISELECT_OCCURRENCES" && !ingestionPoint.use?.registration) {
        return false;
    }

    if (ingestionPoint.authless && INGESTION_AUTHLESS_RESTRICTED_FIELD_TYPE_SET.has(tile.fieldType)) {
        return false;
    }

    return true;
}

export function isPaletteGroupIncludedInUseFilter(
    groupId: IngestionPaletteGroupId,
    filterByUse: IngestionPaletteUseFilter,
): boolean {
    if (filterByUse === "all") {
        return true;
    }
    if (filterByUse === "basic") {
        return BASIC_PALETTE_GROUP_IDS.includes(groupId);
    }
    if (filterByUse === "connect25") {
        return groupId === IngestionPaletteGroupIds.CONNECT25_REFERENCE;
    }
    return groupId === IngestionPaletteGroupIds.LIVE25_FIELDS;
}

export function paletteTileMatchesSearch(tile: IngestionPaletteTile, searchText: string): boolean {
    const normalizedSearch = searchText.trim().toLowerCase();
    if (!normalizedSearch) {
        return true;
    }
    return (
        tile.label.toLowerCase().includes(normalizedSearch) || tile.fieldType.toLowerCase().includes(normalizedSearch)
    );
}

export type VisibleIngestionPaletteGroup = IngestionPaletteGroup & {
    visibleTiles: IngestionPaletteTile[];
};

export function getVisiblePaletteGroups(options: {
    ingestionPoint: IngestionPoint | undefined;
    searchText: string;
    filterByUse: IngestionPaletteUseFilter;
}): VisibleIngestionPaletteGroup[] {
    const { ingestionPoint, searchText, filterByUse } = options;

    return INGESTION_FIELD_PALETTE_GROUPS.map((group) => {
        if (!isPaletteGroupIncludedInUseFilter(group.id, filterByUse)) {
            return { ...group, visibleTiles: [] };
        }

        const visibleTiles = group.tiles.filter(
            (tile) =>
                isPaletteTileVisibleForIngestionPoint(tile, ingestionPoint) &&
                paletteTileMatchesSearch(tile, searchText),
        );

        return { ...group, visibleTiles };
    }).filter((group) => group.visibleTiles.length > 0);
}

export type IngestionPaletteDragPayload = {
    fieldType: FieldType;
    label: string;
};

export const INGESTION_PALETTE_DRAG_DATA_TYPE = "application/x-ingestion-palette-field";

export function serializePaletteDragPayload(payload: IngestionPaletteDragPayload): string {
    return JSON.stringify(payload);
}

export function parsePaletteDragPayload(raw: string | undefined | null): IngestionPaletteDragPayload | undefined {
    if (!raw?.trim()) {
        return undefined;
    }
    try {
        const parsed = JSON.parse(raw) as Partial<IngestionPaletteDragPayload>;
        if (!parsed.fieldType || !parsed.label) {
            return undefined;
        }
        return { fieldType: parsed.fieldType, label: parsed.label };
    } catch {
        return undefined;
    }
}

export function readPaletteDragPayloadFromDataTransfer(
    dataTransfer: DataTransfer | null | undefined,
): IngestionPaletteDragPayload | undefined {
    if (!dataTransfer) {
        return undefined;
    }

    return (
        parsePaletteDragPayload(dataTransfer.getData(INGESTION_PALETTE_DRAG_DATA_TYPE)) ??
        parsePaletteDragPayload(dataTransfer.getData("text/plain"))
    );
}

export function isPaletteDragEvent(event: DragEvent): boolean {
    const transferTypes = event.dataTransfer?.types;
    if (!transferTypes?.length) {
        return false;
    }

    const typeList = Array.from(transferTypes);
    return typeList.includes(INGESTION_PALETTE_DRAG_DATA_TYPE) || typeList.includes("text/plain");
}

export function computePaletteDropInsertIndex(clientY: number, cardElements: HTMLElement[]): number {
    for (let index = 0; index < cardElements.length; index++) {
        const rect = cardElements[index].getBoundingClientRect();
        const midpointY = rect.top + rect.height / 2;
        if (midpointY > clientY) {
            return index;
        }
    }
    return cardElements.length;
}

export function getDefaultCollapsedPaletteGroupIds(): Set<IngestionPaletteGroupId> {
    return new Set(INGESTION_FIELD_PALETTE_GROUPS.filter((group) => !group.defaultExpanded).map((group) => group.id));
}
